/**
 * AdminHustleMeetMatchmakerPage.jsx — /admin/hustlemeet-matchmaker
 *
 * Admin console:
 *   - Live status (autonomy, threshold, rate limit, totals)
 *   - "Run now" (with dry-run toggle)
 *   - Recent invites table with per-row approve button (admin_approve mode)
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { Sparkles, Play, CheckCircle2, RefreshCw } from "lucide-react";
import PlatformHeader from "@/components/PlatformHeader";
import SuiteFooter from "@/components/SuiteFooter";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;
const authHeader = () => {
  const t = localStorage.getItem("token") || localStorage.getItem("access_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const STATUS_COLOUR = {
  sent:            "bg-emerald-500/10 text-emerald-500",
  pending_admin:   "bg-amber-500/10   text-amber-500",
  accepted:        "bg-sky-500/10     text-sky-500",
  declined:        "bg-stone-500/10   text-stone-500",
  expired:         "bg-stone-500/10   text-stone-500",
  blocked:         "bg-red-500/10     text-red-500",
};

export default function AdminHustleMeetMatchmakerPage() {
  const [status,   setStatus]  = useState(null);
  const [invites,  setInvites] = useState([]);
  const [busy,     setBusy]    = useState(false);
  const [dryRun,   setDryRun]  = useState(true);
  const [runOut,   setRunOut]  = useState(null);
  const [err,      setErr]     = useState("");

  const reload = async () => {
    try {
      const [s, i] = await Promise.all([
        axios.get(`${API}/admin/hustlemeet-matchmaker/status`,
          { headers: authHeader(), withCredentials: true }),
        axios.get(`${API}/admin/hustlemeet-matchmaker/invites?limit=100`,
          { headers: authHeader(), withCredentials: true }),
      ]);
      setStatus(s.data);
      setInvites(i.data?.invites || []);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    }
  };

  useEffect(() => {
    document.title = "Admin · HustleMeet Matchmaker";
    reload();
  }, []);

  const runNow = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/hustlemeet-matchmaker/run`,
        { dry_run: dryRun, max_events: 25 },
        { headers: authHeader(), withCredentials: true });
      setRunOut(data);
      toast.success(`Processed ${data.processed || 0} events${dryRun ? " (dry)" : ""}`);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Run failed");
    } finally {
      setBusy(false);
    }
  };

  const approve = async (inv) => {
    try {
      await axios.post(
        `${API}/admin/hustlemeet-matchmaker/invites/${inv.id}/approve`, {},
        { headers: authHeader(), withCredentials: true });
      toast.success("Invite sent.");
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Approve failed");
    }
  };

  if (err) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-[#0a0908] flex items-center justify-center px-6">
        <div className="max-w-md text-center" data-testid="mm-error">
          <p className="text-lg font-black">Access denied</p>
          <p className="mt-2 text-stone-500 text-sm">{err}</p>
        </div>
      </div>
    );
  }
  if (!status) {
    return (
      <div data-testid="mm-loading" className="min-h-screen bg-slate-50 dark:bg-[#0a0908] flex items-center justify-center text-stone-400">
        Loading…
      </div>
    );
  }

  return (
    <div
      data-testid="admin-hustlemeet-matchmaker-page"
      className="min-h-screen bg-slate-50 text-stone-900 dark:bg-[#0a0908] dark:text-stone-100 transition-colors"
    >
      <PlatformHeader
        brand={{ name: "Admin · Matchmaker", to: "/admin/hustlemeet-matchmaker" }}
        showBookCta={false} showCart={false}
      />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-24">
        <header className="mb-8 flex items-start gap-4">
          <div className="w-12 h-12 rounded-2xl bg-amber-500/10 text-amber-500 flex items-center justify-center">
            <Sparkles size={22}/>
          </div>
          <div className="flex-1">
            <p className="text-amber-500 text-[11px] font-black uppercase tracking-[0.32em]">
              AI worker
            </p>
            <h1 className="mt-1 text-2xl md:text-3xl font-black">HustleMeet Matchmaker</h1>
            <p className="mt-1 text-xs text-stone-500">
              Ranks profiles by compatibility, fires personalised invites at the confidence threshold.
            </p>
          </div>
          <Button
            onClick={reload}
            variant="outline"
            data-testid="btn-reload"
            className="rounded-full font-black"
          >
            <RefreshCw size={14} className="mr-1"/> Refresh
          </Button>
        </header>

        {/* Live status card */}
        <section
          data-testid="mm-status-card"
          className="rounded-2xl border border-stone-200 dark:border-stone-800 bg-white dark:bg-slate-950 p-6 mb-4 grid grid-cols-2 sm:grid-cols-4 gap-4"
        >
          <div>
            <p className="text-[10px] uppercase tracking-widest text-stone-500 font-black">Autonomy</p>
            <p className="mt-1 font-black" data-testid="mm-autonomy">{status.autonomy}</p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-widest text-stone-500 font-black">Threshold</p>
            <p className="mt-1 font-black" data-testid="mm-threshold">{status.threshold}</p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-widest text-stone-500 font-black">Rate limit / 14d</p>
            <p className="mt-1 font-black">{status.rate_limit_14d}</p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-widest text-stone-500 font-black">Sent all-time</p>
            <p className="mt-1 font-black" data-testid="mm-sent-total">{status.invites_sent}</p>
          </div>
        </section>

        {/* Run now controls */}
        <section
          data-testid="mm-run-controls"
          className="rounded-2xl border border-amber-300 dark:border-amber-800 bg-gradient-to-br from-amber-400/10 to-orange-500/10 p-6 mb-4"
        >
          <h2 className="font-black">Run now</h2>
          <p className="text-xs text-stone-500 mt-1">
            Scans all Circle events starting in the next 14 days and fills open seats.
          </p>
          <div className="mt-4 flex items-center gap-3 flex-wrap">
            <label className="inline-flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={dryRun}
                onChange={(e) => setDryRun(e.target.checked)}
                data-testid="chk-dry-run"
              />
              Dry-run (no email sent)
            </label>
            <Button
              data-testid="btn-run-now"
              onClick={runNow} disabled={busy}
              className="rounded-full bg-amber-500 text-slate-950 hover:bg-amber-400 font-black"
            >
              <Play size={14} className="mr-1"/>
              {busy ? "Running…" : "Run matchmaker"}
            </Button>
          </div>
          {runOut && (
            <div
              data-testid="mm-run-output"
              className="mt-4 rounded-lg bg-white dark:bg-slate-950 border border-stone-200 dark:border-stone-800 p-3 text-xs font-mono overflow-x-auto"
            >
              <pre className="whitespace-pre-wrap">{JSON.stringify(runOut, null, 2)}</pre>
            </div>
          )}
        </section>

        {/* Invites log */}
        <section data-testid="mm-invites-table">
          <h2 className="font-black text-lg mb-3">Recent invites ({invites.length})</h2>
          {invites.length === 0 && (
            <p className="text-sm text-stone-500">
              No invites yet. Run the matchmaker (dry-run first) to see who it would target.
            </p>
          )}
          {invites.length > 0 && (
            <ul className="space-y-2">
              {invites.map((inv) => (
                <li
                  key={inv.id}
                  data-testid={`mm-invite-${inv.id}`}
                  className="rounded-xl border border-stone-200 dark:border-stone-800 bg-white dark:bg-slate-950 p-4 flex items-center gap-3"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className={`text-[10px] uppercase tracking-widest font-black px-2 py-0.5 rounded-full ${STATUS_COLOUR[inv.status] || "bg-stone-500/10 text-stone-500"}`}>
                        {inv.status}
                      </span>
                      <span className="text-sm font-bold">Score {(inv.score || 0).toFixed(2)}</span>
                    </div>
                    <p className="mt-1 text-xs text-stone-500">
                      Member <strong>{inv.member_id}</strong> · Event <strong>{inv.event_id}</strong>
                    </p>
                  </div>
                  {inv.status === "pending_admin" && (
                    <Button
                      data-testid={`btn-approve-invite-${inv.id}`}
                      onClick={() => approve(inv)}
                      className="rounded-full bg-amber-500 text-slate-950 hover:bg-amber-400 font-black h-9 text-xs"
                    >
                      <CheckCircle2 size={12} className="mr-1"/> Approve
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>

      <SuiteFooter />
    </div>
  );
}
